import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { devtools } from 'zustand/middleware';
import {
  loginUser,
  logoutUser,
  getProfile,
  completeInvitationSetup,
  setToken,
  getToken,
  removeToken,
  setUserData,
  getUserData,
  removeUserData,
  getCurrentInstitutionId
} from '@/services/api';

const useAuthStore = create(
  devtools(
    persist(
      (set, get) => ({
        // ==================== State ====================
        // User data
        user: null,
        token: null,
        institutionMemberId: null,
        
        // User role and institution
        role: null, // 'teacher' or 'student'
        institution: null,
        institutionId: null,
        
        // Auth status
        isAuthenticated: false,
        isLoading: false,
        isInitialized: false,
        
        // Error handling
        error: null,
        
        // Session management
        lastActivity: null,
        sessionExpiry: null,

        // ==================== Actions ====================
        
        // Initialize auth state from storage - PREVENT LOOPS
        initializeAuth: async () => {
          // Prevent multiple initializations
          const state = get();
          if (state.isInitialized) {
            console.log('Auth already initialized, skipping...');
            return;
          }
          
          console.log('=== AUTH STORE INITIALIZATION ===');
          
          const token = getToken();
          const userData = getUserData();
          const institutionId = getCurrentInstitutionId();
          
          console.log('Token exists:', !!token);
          console.log('User data:', userData);
          console.log('Institution ID:', institutionId);
          
          if (token && userData) {
            // Just use stored data, don't make API calls during init
            set({
              user: userData,
              token,
              isAuthenticated: true,
              role: userData.userRole,
              institutionId: userData.institutionId || institutionId,
              institutionMemberId: userData.institutionMemberId,
              lastActivity: new Date().toISOString(),
              isInitialized: true,
            });
            
            // Sync role from localStorage if available
            if (institutionId) {
              const storedRole = localStorage.getItem(`userRole_${institutionId}`);
              if (storedRole) {
                set({ role: storedRole });
              }
            }
            
            console.log('Auth store initialized with existing data');
          } else {
            console.log('No existing auth data found');
            set({ isInitialized: true });
          }
        },

        // Login - SIMPLIFIED TO PREVENT LOOPS
        login: async (email, password) => {
          console.log('=== AUTH STORE LOGIN ===');
          console.log('Email:', email);
          
          set({ isLoading: true, error: null });
          
          try {
            // Get current institution context
            const institutionId = getCurrentInstitutionId();
            
            console.log('Institution ID:', institutionId);
            
            if (!institutionId) {
              throw new Error('No institution selected');
            }
            
            const response = await loginUser(email, password, institutionId);
            console.log('Login response:', response);
            
            if (response.success) {
              const { data } = response;
              
              // Set token and user data in storage
              setToken(data.token);
              const userData = {
                id: data.userId,
                userId: data.userId,
                email: data.email,
                displayName: data.displayName,
                emailVerified: data.emailVerified,
                userRole: data.userRole,
                institutionId: institutionId,
                institutionMemberId: data.institutionMemberId
              };
              setUserData(userData);
              
              // Update store
              set({
                user: userData,
                token: data.token,
                isAuthenticated: true,
                role: data.userRole,
                institutionId: parseInt(institutionId),
                institutionMemberId: data.institutionMemberId,
                lastActivity: new Date().toISOString(),
                error: null,
              });
              
              console.log('Login successful, role:', data.userRole);
              return { success: true, data };  // ✅ FIXED: Return data including language
            } else {
              set({ error: response.message || 'Login failed' });
              return { success: false, message: response.message };
            }
          } catch (error) {
            console.error('Login error:', error);
            const errorMessage = error.message || 'Login failed';
            set({ error: errorMessage });
            return { success: false, message: errorMessage };
          } finally {
            set({ isLoading: false });
          }
        },

        // Logout
        logout: async () => {
          console.log('=== AUTH STORE LOGOUT ===');
          
          try {
            await logoutUser();
          } catch (error) {
            console.error('Logout API error:', error);
          }
          
          // Clear storage
          removeToken();
          removeUserData();
          localStorage.removeItem('selectedInstitution');
          localStorage.removeItem('currentInstitutionId');
          
          // Remove all institution-specific roles
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('userRole_')) {
              localStorage.removeItem(key);
            }
          });
          
          // Reset store
          set({
            user: null,
            token: null,
            institutionMemberId: null,
            role: null,
            institution: null,
            institutionId: null,
            isAuthenticated: false,
            error: null,
            lastActivity: null,
            sessionExpiry: null,
            isLoading: false,
          });
        },

        // Update user profile
        updateProfile: (updates) => {
          set((state) => ({
            user: state.user ? { ...state.user, ...updates } : null,
          }));
        },

        // Check session validity - SIMPLIFIED
        checkSession: async () => {
          const state = get();
          if (!state.isAuthenticated || !state.token) {
            return;
          }
          
          // Simple token presence check - don't make API calls here
          const token = getToken();
          if (!token) {
            console.log('No token found, logging out');
            get().logout();
          }
        },

        // Clear error
        clearError: () => {
          set({ error: null });
        },

        // Update last activity
        updateActivity: () => {
          set({ lastActivity: new Date().toISOString() });
        },

        // ==================== Getters ====================
        
        // Check if user is teacher
        isTeacher: () => {
          const state = get();
          return state.role === 'teacher';
        },

        // Check if user is student
        isStudent: () => {
          const state = get();
          return state.role === 'student';
        },

        // Get formatted user display name
        getUserDisplayName: () => {
          const state = get();
          if (!state.user) return 'Guest';
          return state.user.displayName || state.user.email || 'User';
        },

        // Get institution display name
        getInstitutionName: () => {
          const state = get();
          return state.institution?.name || 'No Institution';
        },
      }),
      {
        name: 'auth-storage',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          // Only persist essential data
          user: state.user,
          token: state.token,
          role: state.role,
          institutionId: state.institutionId,
          institutionMemberId: state.institutionMemberId,
          lastActivity: state.lastActivity,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    ),
    {
      name: 'auth-store',
    }
  )
);

// Session check middleware - SIMPLIFIED TO PREVENT LOOPS
if (typeof window !== 'undefined') {
  // Check session on focus (only if authenticated)
  window.addEventListener('focus', () => {
    const { checkSession, isAuthenticated } = useAuthStore.getState();
    if (isAuthenticated) {
      checkSession();
    }
  });

  // Check session periodically (less frequently)
  setInterval(() => {
    const { checkSession, isAuthenticated } = useAuthStore.getState();
    if (isAuthenticated) {
      checkSession();
    }
  }, 10 * 60 * 1000); // Every 10 minutes instead of 5
}

export default useAuthStore;